import dailyPricesFallback from "../data/dailyPrices.json";
import {
  type IDailyDateInfo,
  type IDailyPrices,
} from "../features/pricing/pricingInterface";
import { type IHotelState } from "../features/hotel/hotelInterface";

export const normalizeDateKey = (date: string): string => date.slice(0, 10);

export const getDailyDateInfo = (
  prices: IDailyPrices | null | undefined,
  hotelCode: IHotelState["HotelCode"],
  date: string,
  currencyCode: string,
): IDailyDateInfo | null => {
  const dateKey = normalizeDateKey(date);
  const day = prices?.[hotelCode]?.dates?.[dateKey];
  if (!day) return null;

  return {
    available: day.available,
    price: day.available ? (day.prices[currencyCode] ?? null) : null,
  };
};

export const isDateUnavailable = (
  prices: IDailyPrices | null | undefined,
  hotelCode: IHotelState["HotelCode"],
  date: string,
): boolean => {
  const dateKey = normalizeDateKey(date);
  const day = prices?.[hotelCode]?.dates?.[dateKey];
  return day?.available === false;
};

export const rangeHasUnavailableDate = (
  prices: IDailyPrices | null | undefined,
  hotelCode: IHotelState["HotelCode"],
  startDate: string,
  endDate: string,
): boolean => {
  const start = normalizeDateKey(startDate);
  const end = normalizeDateKey(endDate);
  const hotelDates = prices?.[hotelCode]?.dates;
  if (!hotelDates) return false;

  return Object.entries(hotelDates).some(([date, day]) => {
    return date >= start && date < end && day.available === false;
  });
};

export const getRangePrice = (
  prices: IDailyPrices | null | undefined,
  hotelCode: IHotelState["HotelCode"],
  startDate: string,
  endDate: string,
  currencyCode: string,
): number | null => {
  const start = normalizeDateKey(startDate);
  const end = normalizeDateKey(endDate);
  const hotelDates = prices?.[hotelCode]?.dates;
  if (!hotelDates) return null;

  let total = 0;
  const current = new Date(`${start}T00:00:00Z`);
  while (current.toISOString().slice(0, 10) < end) {
    const day = hotelDates[current.toISOString().slice(0, 10)];
    const price = day?.available ? day.prices[currencyCode] : undefined;
    if (price == null) return null;
    total += price;
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return total;
};

export const getFallbackDailyPrices = (): IDailyPrices =>
  dailyPricesFallback as IDailyPrices;
